import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, ChevronDown, ChevronRight, Copy } from "lucide-react";
import api, { apiError, API_ROOT } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/time";
import { StatCard } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const STATUS_CLS = { sent: "bg-sky-100 text-sky-700", delivered: "bg-emerald-100 text-emerald-700", failed: "bg-red-100 text-red-700",
  skipped: "bg-slate-100 text-slate-600", bounced: "bg-red-100 text-red-700", soft_bounce: "bg-amber-100 text-amber-700",
  complaint: "bg-rose-100 text-rose-700", rejected: "bg-red-100 text-red-700", delayed: "bg-amber-100 text-amber-700", sending: "bg-slate-100 text-slate-600" };
const STATUSES = ["sent", "delivered", "failed", "skipped", "bounced", "soft_bounce", "complaint", "rejected"];
const PROV = { ses: "Amazon SES", turbosmtp: "TurboSMTP" };
const label = (s) => s.replace("_", " ");

function Pick({ value, onChange, options, all, testId }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="w-44" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Row({ r, kinds }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr onClick={() => setOpen((o) => !o)} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" data-testid={`email-log-row-${r.id}`}>
        <td className="px-3 py-2.5 text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td className="whitespace-nowrap text-slate-600">{fmtDateTime(r.created_at)}</td>
        <td className="max-w-[220px] truncate font-semibold text-slate-800">{r.to}</td>
        <td className="max-w-[280px] truncate text-slate-600">{r.subject}</td>
        <td className="text-slate-600">{kinds[r.kind] || r.kind}</td>
        <td><span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${STATUS_CLS[r.status] || ""}`} data-testid={`email-log-status-${r.id}`}>{label(r.status)}</span></td>
        <td className="whitespace-nowrap text-slate-600">{PROV[r.provider] || "—"}{r.failover_used && <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">failover</span>}</td>
      </tr>
      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/60"><td colSpan={7} className="px-10 py-3 text-sm" data-testid={`email-log-detail-${r.id}`}>
          <div className="grid gap-4 md:grid-cols-3">
            <div><p className="font-bold text-slate-800">Details</p>
              <p className="text-slate-600">Organisation: {r.org_name || "—"}</p>
              {r.message_id && <p className="break-all text-slate-600">Message ID: {r.message_id}</p>}
              {r.error && <p className="mt-1 rounded bg-red-50 p-2 text-xs text-red-700">{r.error}</p>}</div>
            <div><p className="font-bold text-slate-800">Send attempts</p>
              {(r.attempts || []).length === 0 ? <p className="text-slate-400">None</p> : r.attempts.map((a, i) => (
                <p key={`${a.provider}-${a.at}`} className={a.ok ? "text-emerald-700" : "text-red-700"}>{i + 1}. {PROV[a.provider]}: {a.ok ? "accepted" : a.error} <span className="text-slate-400">· {fmtDateTime(a.at)}</span></p>))}</div>
            <div><p className="font-bold text-slate-800">Delivery events</p>
              {(r.events || []).length === 0 ? <p className="text-slate-400">{r.provider === "turbosmtp" ? "TurboSMTP doesn't report delivery events." : "No events reported yet."}</p>
                : r.events.map((e) => <p key={`${e.at}-${e.detail}`} className="text-slate-700">{e.detail} <span className="text-slate-400">· {fmtDateTime(e.at)}</span></p>)}</div>
          </div>
        </td></tr>
      )}
    </>
  );
}

export default function EmailLog() {
  const [f, setF] = useState({ status: "", provider: "", kind: "", days: "30", q: "", failover: false });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [d, setD] = useState(null);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== "" && v !== false)).toString();

  const load = useCallback(() => {
    api.get(`/platform/email-log?${qs}&skip=${page * 50}&limit=50`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [qs, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { setPage(0); setF((c) => ({ ...c, q })); }, 350); return () => clearTimeout(t); }, [q]);
  const set = (k) => (v) => { setPage(0); setF((c) => ({ ...c, [k]: v })); };

  if (!d) return <div className="text-slate-400">Loading…</div>;
  const c = d.counts;
  return (
    <div className="space-y-5" data-testid="email-log">
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Total (30 days)" value={c.total} testId="elog-total" onClick={() => setF((x) => ({ ...x, status: "", failover: false }))} />
        <StatCard label="Sent / delivered" value={c.sent + c.delivered} accent="#059669" testId="elog-sent" onClick={() => set("status")("sent")} />
        <StatCard label="Failed" value={c.failed} accent="#DC2626" testId="elog-failed" onClick={() => set("status")("failed")} />
        <StatCard label="Bounced" value={c.bounced + c.soft_bounce} accent="#EA580C" testId="elog-bounced" onClick={() => set("status")("bounced")} />
        <StatCard label="Complaints" value={c.complaint} accent="#E11D48" testId="elog-complaints" onClick={() => set("status")("complaint")} />
        <StatCard label="Failover used" value={c.failover} accent="#D97706" testId="elog-failover" onClick={() => set("failover")(true)} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" className="w-64 pl-8" data-testid="elog-search" /></div>
        <Pick value={f.status} onChange={set("status")} all="All statuses" options={STATUSES.map((s) => [s, label(s)])} testId="elog-status" />
        <Pick value={f.provider} onChange={set("provider")} all="All providers" options={Object.entries(PROV)} testId="elog-provider" />
        <Pick value={f.kind} onChange={set("kind")} all="All types" options={Object.entries(d.kinds)} testId="elog-kind" />
        <Pick value={f.days === "30" ? "" : f.days} onChange={(v) => set("days")(v || "30")} all="Last 30 days" options={[["1", "Last 24 hours"], ["7", "Last 7 days"]]} testId="elog-days" />
        {f.failover && <button onClick={() => set("failover")(false)} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700" data-testid="elog-clear-failover">Failover only ×</button>}
        <a href={`${API_ROOT}/platform/email-log.csv?${qs}`} className="ml-auto"><Button variant="outline" data-testid="elog-export"><Download className="mr-1 h-4 w-4" />Export CSV</Button></a>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="elog-table">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="w-8 px-3 py-3" /><th>Sent</th><th>To</th><th>Subject</th><th>Type</th><th>Status</th><th>Provider</th></tr></thead>
          <tbody>
            {d.items.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="elog-empty">No emails match these filters.</td></tr>
              : d.items.map((r) => <Row key={r.id} r={r} kinds={d.kinds} />)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span data-testid="elog-count">{d.total} email{d.total === 1 ? "" : "s"} · kept for 30 days</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="elog-prev">Previous</Button>
          <Button size="sm" variant="outline" disabled={(page + 1) * 50 >= d.total} onClick={() => setPage((p) => p + 1)} data-testid="elog-next">Next</Button>
        </div>
      </div>
      <WebhookHelp url={d.webhook_url} />
    </div>
  );
}

function WebhookHelp({ url }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm" data-testid="elog-webhook-help">
      <p className="font-head font-bold text-slate-900">Track SES bounces & complaints</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-slate-600">
        <li>In AWS SNS (af-south-1), create a topic, then add an <b>HTTPS</b> subscription with the URL below. Servantage confirms it automatically.</li>
        <li>In SES, open your <b>configuration set</b> (or the identity's notifications) and send <b>Bounce, Complaint, Delivery and Reject</b> events to that topic, with original headers included.</li>
      </ol>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-slate-100 px-3 py-2 text-xs text-slate-700" data-testid="elog-webhook-url">{url}</code>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Webhook URL copied"); }} data-testid="elog-webhook-copy"><Copy className="h-4 w-4" /></Button>
      </div>
      <p className="mt-2 text-xs text-slate-400">Keep this URL private: it contains a secret token.</p>
    </div>
  );
}
